import type { PayloadAction } from "@reduxjs/toolkit";
import { arrayToMap } from "../../../helpers/catalogueHelpers";
import type { PhoneCountry } from "../../../phoneCountry";

/**
 * Shared parts of LoginSlice — plan §5.2. This is THE gate module: 26 web slices import
 * `userLogout` / `supportLogout` from it.
 *
 * 24 of the 25 mobile reducers are byte-identical to web's and live here.
 * `getLoginResponseSuccess` is authored per platform (mobile additionally sets
 * `isLoginDone`), and web's 12 extra reducers live in index.desktop.ts.
 *
 * ⚠️ `arrayToMap` is itself a PLATFORM SPLIT (web returns `null` on empty input, mobile
 * returns `{}`), and `fetchOfflineUsersSuccess` below depends on that difference. Importing
 * it from a shared file is a deliberate exception to CLAUDE.md's "shared files must only
 * import shared/pure modules": the bundlers resolve the folder from THIS file's import per
 * platform, so web gets the null-returning variant and mobile the {}-returning one — which
 * is exactly what each app does today. Verified with an enhanced-resolve probe from this
 * directory; do not "fix" it by inlining a single variant.
 */
/**
 * The two logout actions every self-resetting slice listens for.
 *
 * 🔴 **SHARED — they used to be web-only, and the comment here used to say they must never be
 * added to the other platform.** That warning was about the BLAST RADIUS, not the actions: most
 * web slices reset themselves on these (the exact figure is in the table below), so publishing
 * the creators alongside a set of
 * `extraReducers` nobody had reviewed would have changed the other platform's logout app-wide.
 * They are `createAction` calls — pure, platform-free, and INERT until something dispatches
 * them. Defining them on one platform only was what forced every logout-resetting slice to
 * stay split.
 *
 * 🔴 **BOTH platforms dispatch them. This comment used to say mobile did not, and that was
 * wrong when it was written** (corrected 2026-09-08). `apps/mobile`'s logout —
 * `helper/commonHelpers#checkSupportLogout`, the app's only definition — dispatches
 * `supportLogout()` on the support-login branch and `userLogout()` on the ordinary one, exactly
 * as web's does, and has since DP-8110 (c92d2c698, 2026-09-03).
 *
 * What still differs is the BLAST RADIUS, which is what the old warning was really about. A
 * dispatch resets only a slice that has DECLARED a case, and the two platforms have declared
 * very different numbers of them (measured 2026-09-08):
 *
 * | | slices with a case |
 * |---|---|
 * | web | 27 — 26 `.desktop.ts` variants plus `RecommenderSlice/shared.ts` |
 * | mobile | 3 — `RecommenderSlice` (`shared.ts`, so both platforms), `Loyalty` (real bodies), `CustomerSlice` (empty bodies, mirroring web's) |
 *
 * So a mobile logout today clears the loyalty programme cache and the recommender config, and
 * nothing else. Bringing the other 24 across is a real fix for real state leakage and still an
 * app-wide behaviour change that needs its own ticket and its own QA — adding the next native
 * `extraReducer` stays a separate, deliberate decision each time. **What it is no longer
 * blocked on is the dispatch.**
 *
 * The action type strings are the public contract. Do not rename them.
 */
export { userLogout, supportLogout } from "./actions";

export interface SharedLoginState {
  loginResponse: {
    [key: string]: any;
  };
  activeUsers: [];
  logoutResponse: any;
  isLoginDataFetching: boolean;
  forgotPinFirstRes: any;
  validateEmailRes: any;
  isUserSwitching: boolean;
  switchUserInfo: {
    [key: string]: any;
  };
  showLogoutAlert: boolean;
  lastLoggedInUser: any;
  offlineUsers: {
    [key: string]: any;
  };
  isOfflineUsersFetching: boolean;
  openAccountControlModal: boolean;

  phoneCountries: PhoneCountry[];
  isPhoneCountriesFetching: boolean;
  hasFetchedPhoneCountries: boolean;
}

export const sharedInitialState: SharedLoginState = {
  loginResponse: {},
  activeUsers: [],
  logoutResponse: "",
  isLoginDataFetching: false,
  forgotPinFirstRes: {},
  validateEmailRes: {},
  isUserSwitching: false,
  switchUserInfo: {},
  showLogoutAlert: false,
  lastLoggedInUser: {},
  offlineUsers: {},
  isOfflineUsersFetching: false,
  openAccountControlModal: false,

  phoneCountries: [],
  isPhoneCountriesFetching: false,
  hasFetchedPhoneCountries: false,
};

/** Byte-identical in both apps. `action` must be `PayloadAction<any>`, never `any` — see the ledger. */
export const sharedReducers = {
  setPhoneCountries: (
  state: SharedLoginState,
  action: PayloadAction<PhoneCountry[]>
) => {
  state.phoneCountries = action.payload;
  state.isPhoneCountriesFetching = false;
  state.hasFetchedPhoneCountries = true;
},

setPhoneCountriesError: (state: SharedLoginState) => {
  state.isPhoneCountriesFetching = false;
  state.hasFetchedPhoneCountries = true;
},

fetchPhoneCountries: (state: SharedLoginState) => {
  if (state.isPhoneCountriesFetching) return;
  state.isPhoneCountriesFetching = true;
},

  getLoginRespoonse: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.isLoginDataFetching = true;
  },
  getActiveUsers: (state: SharedLoginState) => {
    state.isLoginDataFetching = true;
  },
  getActiveUsersSuccess: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.activeUsers = action.payload;
    state.isLoginDataFetching = false;
  },
  logoutUser: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.isLoginDataFetching = true;
  },
  logoutUserSuccess: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.logoutResponse = action.payload;
    state.isLoginDataFetching = false;
  },
  forgotPinFirstResponse: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.isLoginDataFetching = true;
  },
  forgotPinFirstResponseSuccess: (state: SharedLoginState, action: PayloadAction<any>) => {
    // debugger;
    state.forgotPinFirstRes = action.payload;
  },
  emptyForgotPinFirstResponse: (state: SharedLoginState) => {
    state.forgotPinFirstRes = {};
  },
  validateEmail: (state: SharedLoginState, action: PayloadAction<any>) => {},
  validateEmailSuccess: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.validateEmailRes = action.payload;
  },
  emptyVaildateEmailRes: (state: SharedLoginState) => {
    state.validateEmailRes = {};
  },
  switchUser: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.isUserSwitching = true;
  },
  switchUserSuccess: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.switchUserInfo = action.payload;
    state.isUserSwitching = false;
  },
  resetSwitchUser: (state: SharedLoginState) => {
    state.switchUserInfo = {};
  },
  resetLogout: (state: SharedLoginState) => {
    state.logoutResponse = {};
  },
  showLogoutToast: (state: SharedLoginState) => {
    state.showLogoutAlert = true;
  },
  hideLogoutToast: (state: SharedLoginState) => {
    state.showLogoutAlert = false;
  },
  clearLoginResponse: (state: SharedLoginState) => {
    state.loginResponse = {};
    state.logoutResponse = "";
  },
  setLastLoggedInUser: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.lastLoggedInUser = action.payload;
  },
  removeLastLoggedInUser: (state: SharedLoginState) => {
    state.lastLoggedInUser = {};
  },
  fetchOfflineUsers: (state: SharedLoginState) => {
    state.isOfflineUsersFetching = true;
  },
  fetchOfflineUsersSuccess: (state: SharedLoginState, action: PayloadAction<any>) => {
    state.offlineUsers = arrayToMap(action.payload, "businessUserId");
    state.isOfflineUsersFetching = false;
  },
  openAccountControl: (state: SharedLoginState) => {
    state.openAccountControlModal = true;
  },
  closeAccountControl: (state: SharedLoginState) => {
    state.openAccountControlModal = false;
  },
};
